import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import {
    createFoodOrder,
    getFoodMenu,
    getKitchenOrders,
    searchPersonsForFood,
} from "../../../api/foodApi";

import type {
    FoodMenuItem,
    FoodOrder,
    FoodPersonSearchResult,
} from "./foodTypes";

import {
    estimateNewOrderMinutes,
    formatEstimatedTime,
    getEstimatedReadyAt,
    MICROWAVE_COUNT,
    MINUTES_PER_BOX,
} from "./foodEstimate";

import "./playerLoungeFood.css";

const FIND_PERSON_PIN = "1793";

export default function FoodOrderPage() {
    const { eventId } = useParams<{ eventId: string }>();

    const parsedEventId = Number(eventId);

    const [menu, setMenu] = useState<FoodMenuItem[]>([]);
    const [cart, setCart] = useState<Record<number, number>>({});
    const [category, setCategory] = useState("Alle");
    const [note, setNote] = useState("");
    const [cardCode, setCardCode] = useState("");
    const [scanMode, setScanMode] = useState(false);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState("");
    const [scheduledFor, setScheduledFor] = useState("");
    const [kitchenOrders, setKitchenOrders] = useState<FoodOrder[]>([]);

    const [identifyMode, setIdentifyMode] =
        useState<"scan" | "find">("scan");

    const [personSearchUnlocked, setPersonSearchUnlocked] =
        useState(false);

    const [personSearchPin, setPersonSearchPin] =
        useState("");

    const [personSearch, setPersonSearch] =
        useState("");

    const [personResults, setPersonResults] =
        useState<FoodPersonSearchResult[]>([]);

    const [selectedPerson, setSelectedPerson] =
        useState<FoodPersonSearchResult | null>(null);

    const [personSearching, setPersonSearching] =
        useState(false);

    const scanInputRef = useRef<HTMLInputElement>(null);
    const [manualQty, setManualQty] =
        useState<Record<number, string>>({});

    // ---------------------------------------------------------
    // LOAD MENU
    // ---------------------------------------------------------

    useEffect(() => {
        console.log("FoodOrderPage eventId:", eventId);
        console.log("FoodOrderPage parsedEventId:", parsedEventId);

        if (
            !eventId ||
            !Number.isFinite(parsedEventId) ||
            parsedEventId <= 0
        ) {
            setMenu([]);
            setMessage("Event ID mangler eller er ugyldigt.");
            return;
        }

        setMessage("");

        getFoodMenu(parsedEventId)
            .then(data => {
                console.log("Food menu response:", data);

                const safeData = Array.isArray(data)
                    ? data
                    : (data as any)?.$values ?? [];

                console.log("Food menu safe data:", safeData);

                setMenu(safeData);
            })
            .catch(err => {
                console.error("Kunne ikke hente food menu:", err);

                setMenu([]);

                setMessage(
                    err instanceof Error
                        ? err.message
                        : "Kunne ikke hente menu."
                );
            });
    }, [eventId, parsedEventId]);

    // ---------------------------------------------------------
    // LOAD KITCHEN QUEUE FOR ESTIMATED TIME
    // ---------------------------------------------------------

    useEffect(() => {
        if (
            !Number.isFinite(parsedEventId) ||
            parsedEventId <= 0
        ) {
            setKitchenOrders([]);
            return;
        }

        let mounted = true;

        const loadKitchenQueue = async () => {
            try {
                const data =
                    await getKitchenOrders(
                        parsedEventId
                    );

                if (mounted) {
                    setKitchenOrders(
                        Array.isArray(data)
                            ? data
                            : []
                    );
                }
            } catch (err) {
                console.error(
                    "Kunne ikke hente kø til tidsestimat:",
                    err
                );
            }
        };

        loadKitchenQueue();

        const timer =
            window.setInterval(
                loadKitchenQueue,
                5000
            );

        return () => {
            mounted = false;

            window.clearInterval(
                timer
            );
        };
    }, [parsedEventId]);

    // ---------------------------------------------------------
    // FOCUS SCANNER
    // ---------------------------------------------------------

    useEffect(() => {
        if (
            !scanMode ||
            identifyMode !== "scan"
        ) {
            return;
        }

        window.setTimeout(() => {
            scanInputRef.current?.focus();
        }, 50);
    }, [scanMode, identifyMode]);

    // ---------------------------------------------------------
    // FIND PERSON
    // ---------------------------------------------------------

    useEffect(() => {
        if (
            !scanMode ||
            identifyMode !== "find" ||
            !personSearchUnlocked
        ) {
            return;
        }

        const q =
            personSearch.trim();

        if (q.length < 2) {
            setPersonResults([]);
            setPersonSearching(false);
            return;
        }

        let cancelled = false;

        const timer =
            window.setTimeout(
                async () => {
                    setPersonSearching(true);

                    try {
                        const persons =
                            await searchPersonsForFood(
                                parsedEventId,
                                q
                            );

                        if (!cancelled) {
                            setPersonResults(
                                persons
                            );
                        }
                    } catch (err) {
                        console.error(
                            "Kunne ikke søge personer:",
                            err
                        );

                        if (!cancelled) {
                            setPersonResults([]);
                            setMessage(
                                err instanceof Error
                                    ? err.message
                                    : "Kunne ikke søge personer."
                            );
                        }
                    } finally {
                        if (!cancelled) {
                            setPersonSearching(false);
                        }
                    }
                },
                300
            );

        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [
        scanMode,
        identifyMode,
        personSearchUnlocked,
        personSearch,
        parsedEventId,
    ]);

    // ---------------------------------------------------------
    // CATEGORIES
    // ---------------------------------------------------------

    const categories = useMemo(() => {
        return [
            "Alle",
            ...Array.from(
                new Set(
                    menu
                        .map(x => x.category)
                        .filter(Boolean)
                )
            ),
        ];
    }, [menu]);

    // ---------------------------------------------------------
    // FILTERED MENU
    // ---------------------------------------------------------

    const visibleMenu = useMemo(() => {
        if (category === "Alle")
            return menu;

        return menu.filter(
            x => x.category === category
        );
    }, [menu, category]);

    // ---------------------------------------------------------
    // CART
    // ---------------------------------------------------------

    const cartItems = useMemo(() => {
        return menu
            .filter(
                item =>
                    (cart[item.id] ?? 0) > 0
            )
            .map(item => ({
                ...item,
                quantity:
                    cart[item.id] ?? 0,
            }));
    }, [menu, cart]);

    const total = cartItems.reduce(
        (sum, item) =>
            sum +
            item.price *
            item.quantity,
        0
    );

    const count = cartItems.reduce(
        (sum, item) =>
            sum +
            item.quantity,
        0
    );

    // Lige nu svarer antal varer til antal bokse.
    const estimatedMinutes =
        estimateNewOrderMinutes(
            kitchenOrders,
            count
        );

    const estimatedReadyAt =
        getEstimatedReadyAt(
            estimatedMinutes
        );

    // ---------------------------------------------------------
    // CART ACTIONS
    // ---------------------------------------------------------

    function add(id: number) {
        setCart(prev => ({
            ...prev,
            [id]:
                (prev[id] ?? 0) + 1,
        }));
    }

    function change(
        id: number,
        diff: number
    ) {
        setCart(prev => {
            const current =
                prev[id] ?? 0;

            const next =
                Math.max(
                    0,
                    current + diff
                );

            const updated = {
                ...prev,
            };

            if (next === 0) {
                delete updated[id];

                return updated;
            }

            updated[id] = next;

            return updated;
        });
    }

    function clearCart() {
        setCart({});
        setNote("");
        setScheduledFor("");
    }

    function addQuantity(
        id: number
    ) {
        const quantity =
            Number(manualQty[id]);

        if (
            !Number.isFinite(quantity) ||
            quantity <= 0
        ) {
            return;
        }

        setCart(prev => ({
            ...prev,
            [id]:
                (prev[id] ?? 0) +
                Math.floor(quantity),
        }));

        setManualQty(prev => ({
            ...prev,
            [id]: "",
        }));
    }

    function resetIdentifyModal() {
        setScanMode(false);
        setIdentifyMode("scan");
        setCardCode("");
        setPersonSearchPin("");
        setPersonSearchUnlocked(false);
        setPersonSearch("");
        setPersonResults([]);
        setSelectedPerson(null);
    }

    function unlockPersonSearch() {
        if (
            personSearchPin !==
            FIND_PERSON_PIN
        ) {
            setMessage(
                "Forkert kode."
            );
            return;
        }

        setMessage("");
        setPersonSearchUnlocked(true);
        setPersonSearchPin("");
    }

    // ---------------------------------------------------------
    // CREATE ORDER
    // ---------------------------------------------------------

    async function submitScannedOrder(
        rawCode = cardCode
    ) {
        if (
            !Number.isFinite(parsedEventId) ||
            parsedEventId <= 0
        ) {
            setMessage(
                "Event ID mangler eller er ugyldigt."
            );

            return;
        }

        if (!rawCode.trim()) {
            setMessage(
                "Scan eller indtast et ID-kort."
            );

            scanInputRef.current?.focus();

            return;
        }

        if (cartItems.length === 0) {
            setMessage(
                "Der er ingen varer i ordren."
            );

            return;
        }

        setBusy(true);
        setMessage("");

        try {
            console.log(
                "Creating food order:",
                {
                    eventId:
                        parsedEventId,

                    cardCode:
                        rawCode.trim(),

                    note,

                    items:
                        cartItems.map(
                            item => ({
                                menuItemId:
                                    item.id,

                                quantity:
                                    item.quantity,
                            })
                        ),
                }
            );

            const order =
                await createFoodOrder(
                    parsedEventId,
                    {
                        cardCode:
                            rawCode.trim(),

                        personId:
                            null,

                        note:
                            note.trim(),

                        scheduledFor:
                            scheduledFor ||
                            null,

                        items:
                            cartItems.map(
                                item => ({
                                    menuItemId:
                                        item.id,

                                    quantity:
                                        item.quantity,
                                })
                            ),
                    }
                );

            console.log(
                "Food order created:",
                order
            );

            setMessage(
                `Ordre #${order.id} er oprettet.`
            );

            setCart({});
            setNote("");
            setScheduledFor("");
            setCardCode("");
            resetIdentifyModal();

        } catch (err) {
            console.error(
                "Kunne ikke oprette ordren:",
                err
            );

            setMessage(
                err instanceof Error
                    ? err.message
                    : "Kunne ikke oprette ordren."
            );

            scanInputRef.current?.focus();

        } finally {
            setBusy(false);
        }
    }

    async function submitSelectedPersonOrder() {
        if (
            !selectedPerson
        ) {
            setMessage(
                "Vælg en person først."
            );
            return;
        }

        if (
            cartItems.length === 0
        ) {
            setMessage(
                "Der er ingen varer i ordren."
            );
            return;
        }

        setBusy(true);
        setMessage("");

        try {
            const order =
                await createFoodOrder(
                    parsedEventId,
                    {
                        cardCode: "",

                        personId:
                            selectedPerson.personId,

                        personName:
                            selectedPerson.displayName,

                        note:
                            note.trim(),

                        scheduledFor:
                            scheduledFor ||
                            null,

                        items:
                            cartItems.map(
                                item => ({
                                    menuItemId:
                                        item.id,

                                    quantity:
                                        item.quantity,
                                })
                            ),
                    }
                );

            setMessage(
                `Ordre #${order.id} er oprettet til ${selectedPerson.displayName}.`
            );

            setCart({});
            setNote("");
            setScheduledFor("");

            resetIdentifyModal();
        } catch (err) {
            console.error(
                "Kunne ikke oprette ordre:",
                err
            );

            setMessage(
                err instanceof Error
                    ? err.message
                    : "Kunne ikke oprette ordren."
            );
        } finally {
            setBusy(false);
        }
    }

    // ---------------------------------------------------------
    // UI
    // ---------------------------------------------------------

    return (
        <>
            <header className="pl-topbar">
                <div>
                    <h1>
                        Ny ordre
                    </h1>

                    <div className="pl-steps">
                        1. Vælg mad & drikke
                        {" → "}
                        2. Scan ID-kort eller find person
                    </div>
                </div>
            </header>

            <div className="pl-order-layout">

                {/* MENU */}

                <section>
                    <div className="pl-section-title-row">
                        <h2>
                            1. Vælg mad & drikke
                        </h2>

                        <div className="pl-tabs">
                            {categories.map(
                                item => (
                                    <button
                                        key={item}
                                        onClick={() =>
                                            setCategory(
                                                item
                                            )
                                        }
                                        className={
                                            category ===
                                                item
                                                ? "active"
                                                : ""
                                        }
                                    >
                                        {item}
                                    </button>
                                )
                            )}
                        </div>
                    </div>

                    {message &&
                        menu.length === 0 && (
                            <div className="pl-message">
                                {message}
                            </div>
                        )}

                    {menu.length === 0 &&
                        !message ? (
                        <div className="pl-empty-state">
                            <strong>
                                Ingen produkter
                            </strong>

                            <span>
                                Der er ikke
                                oprettet nogen
                                aktive produkter
                                til dette event.
                            </span>
                        </div>
                    ) : (
                        <div className="pl-menu-grid">
                            {visibleMenu.map(
                                item => {
                                    const quantity =
                                        cart[
                                        item.id
                                        ] ?? 0;

                                    return (
                                        <article
                                            className={`pl-product ${quantity >
                                                0
                                                ? "selected"
                                                : ""
                                                }`}
                                            key={
                                                item.id
                                            }
                                            role="button"
                                            tabIndex={
                                                0
                                            }
                                            onClick={() =>
                                                add(
                                                    item.id
                                                )
                                            }
                                            onKeyDown={
                                                e => {
                                                    if (
                                                        e.key ===
                                                        "Enter" ||
                                                        e.key ===
                                                        " "
                                                    ) {
                                                        e.preventDefault();

                                                        add(
                                                            item.id
                                                        );
                                                    }
                                                }
                                            }
                                        >
                                            {/* QUANTITY BADGE */}

                                            {quantity >
                                                0 && (
                                                    <div className="pl-product-count">
                                                        {
                                                            quantity
                                                        }
                                                    </div>
                                                )}

                                            {/* IMAGE */}

                                            <div className="pl-product-image">
                                                {item.imageUrl ? (
                                                    <img
                                                        src={
                                                            item.imageUrl
                                                        }
                                                        alt={
                                                            item.name
                                                        }
                                                    />
                                                ) : (
                                                    <span>
                                                        {item.name
                                                            .slice(
                                                                0,
                                                                1
                                                            )
                                                            .toUpperCase()}
                                                    </span>
                                                )}
                                            </div>

                                            {/* BODY */}

                                            <div className="pl-product-body">
                                                <div className="pl-product-info">
                                                    <strong>
                                                        {item.name}
                                                    </strong>

                                                    <span>
                                                        {item.price.toFixed(0)} kr.
                                                    </span>
                                                </div>

                                                {quantity === 0 ? (
                                                    <button
                                                        type="button"
                                                        className="pl-product-add"
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            add(item.id);
                                                        }}
                                                    >
                                                        +
                                                    </button>
                                                ) : (
                                                    <div
                                                        className="pl-product-qty"
                                                        onClick={e =>
                                                            e.stopPropagation()
                                                        }
                                                    >
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                change(
                                                                    item.id,
                                                                    -1
                                                                )
                                                            }
                                                        >
                                                            −
                                                        </button>

                                                        <span>
                                                            {quantity}
                                                        </span>

                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                change(
                                                                    item.id,
                                                                    1
                                                                )
                                                            }
                                                        >
                                                            +
                                                        </button>
                                                    </div>
                                                )}

                                                <div
                                                    className="pl-product-bulk"
                                                    onClick={e =>
                                                        e.stopPropagation()
                                                    }
                                                >
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        placeholder="Antal"
                                                        value={
                                                            manualQty[item.id] ?? ""
                                                        }
                                                        onChange={e =>
                                                            setManualQty(prev => ({
                                                                ...prev,
                                                                [item.id]:
                                                                    e.target.value,
                                                            }))
                                                        }
                                                        onKeyDown={e => {
                                                            if (
                                                                e.key === "Enter"
                                                            ) {
                                                                e.preventDefault();

                                                                addQuantity(
                                                                    item.id
                                                                );
                                                            }
                                                        }}
                                                    />

                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            addQuantity(
                                                                item.id
                                                            )
                                                        }
                                                    >
                                                        Tilføj
                                                    </button>
                                                </div>
                                            </div>
                                        </article>
                                    );
                                }
                            )}
                        </div>
                    )}
                </section>

                {/* CART */}

                <aside className="pl-cart">
                    <div className="pl-cart-title">
                        <h2>
                            Din ordre
                        </h2>

                        <span>
                            {count}
                        </span>
                    </div>

                    <div className="pl-cart-lines">
                        {cartItems.length ===
                            0 && (
                                <div className="pl-empty">
                                    Ingen varer valgt
                                    endnu.
                                </div>
                            )}

                        {cartItems.map(
                            item => (
                                <div
                                    className="pl-cart-line"
                                    key={
                                        item.id
                                    }
                                >
                                    <div>
                                        <strong>
                                            {
                                                item.name
                                            }
                                        </strong>

                                        <small>
                                            {item.price.toFixed(
                                                0
                                            )}{" "}
                                            kr.
                                        </small>
                                    </div>

                                    <div className="pl-qty">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                change(
                                                    item.id,
                                                    -1
                                                )
                                            }
                                        >
                                            −
                                        </button>

                                        <span>
                                            {
                                                item.quantity
                                            }
                                        </span>

                                        <button
                                            type="button"
                                            onClick={() =>
                                                change(
                                                    item.id,
                                                    1
                                                )
                                            }
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>
                            )
                        )}
                    </div>

                    <textarea
                        placeholder="Bemærkning (valgfri) – fx ingen chili..."
                        value={note}
                        onChange={e =>
                            setNote(
                                e.target.value
                            )
                        }
                    />

                    <div
                        style={{
                            display: "grid",
                            gap: "6px",
                            marginTop: "12px",
                        }}
                    >
                        <label
                            style={{
                                fontWeight: 700,
                            }}
                        >
                            Forudbestilling (valgfri)
                        </label>

                        <input
                            type="datetime-local"
                            value={scheduledFor}
                            onChange={e =>
                                setScheduledFor(
                                    e.target.value
                                )
                            }
                        />

                        {scheduledFor && (
                            <button
                                type="button"
                                className="pl-secondary"
                                onClick={() =>
                                    setScheduledFor("")
                                }
                            >
                                Fjern tidspunkt
                            </button>
                        )}
                    </div>

                    {count > 0 && !scheduledFor && (
                        <div
                            style={{
                                marginTop: "14px",
                                padding: "12px",
                                borderRadius: "10px",
                                background:
                                    "rgba(65, 105, 225, 0.10)",
                            }}
                        >
                            <strong>
                                Estimeret klar om ca.{" "}
                                {estimatedMinutes} min.
                            </strong>

                            <div
                                style={{
                                    marginTop: "4px",
                                    fontSize: "13px",
                                    opacity: 0.8,
                                }}
                            >
                                Ca. kl.{" "}
                                {formatEstimatedTime(
                                    estimatedReadyAt
                                )}
                                {" · "}
                                {MICROWAVE_COUNT} mikroovne
                                {" · "}
                                {MINUTES_PER_BOX} min. pr. boks
                            </div>
                        </div>
                    )}

                    <div className="pl-total">
                        <span>
                            Total
                        </span>

                        <strong>
                            {total.toFixed(
                                0
                            )}{" "}
                            kr.
                        </strong>
                    </div>

                    <button
                        type="button"
                        className="pl-primary"
                        disabled={
                            cartItems.length ===
                            0
                        }
                        onClick={() => {
                            setMessage("");
                            setScanMode(
                                true
                            );
                        }}
                    >
                        Næste: Vælg person →
                    </button>

                    <button
                        type="button"
                        className="pl-secondary"
                        disabled={
                            cartItems.length ===
                            0
                        }
                        onClick={
                            clearCart
                        }
                    >
                        Ryd ordre
                    </button>

                    {message &&
                        menu.length > 0 && (
                            <div className="pl-message">
                                {message}
                            </div>
                        )}
                </aside>
            </div>

            {/* SCAN MODAL */}

            {scanMode && (
                <div
                    className="pl-modal-backdrop"
                    onMouseDown={e => {
                        if (
                            e.target ===
                            e.currentTarget
                        ) {
                            resetIdentifyModal();
                        }
                    }}
                >
                    <div className="pl-scan-modal">
                        <button
                            type="button"
                            className="pl-close"
                            onClick={
                                resetIdentifyModal
                            }
                        >
                            ×
                        </button>

                        <div className="pl-scan-icon">
                            {identifyMode === "scan"
                                ? "▣"
                                : "👤"}
                        </div>

                        <h2>
                            Vælg person
                        </h2>

                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns:
                                    "1fr 1fr",
                                gap: "8px",
                                width: "100%",
                                marginBottom: "16px",
                            }}
                        >
                            <button
                                type="button"
                                className={
                                    identifyMode === "scan"
                                        ? "pl-primary"
                                        : "pl-secondary"
                                }
                                onClick={() => {
                                    setIdentifyMode(
                                        "scan"
                                    );
                                    setMessage("");
                                }}
                            >
                                Scan ID-kort
                            </button>

                            <button
                                type="button"
                                className={
                                    identifyMode === "find"
                                        ? "pl-primary"
                                        : "pl-secondary"
                                }
                                onClick={() => {
                                    setIdentifyMode(
                                        "find"
                                    );
                                    setMessage("");
                                }}
                            >
                                Find person
                            </button>
                        </div>

                        {identifyMode === "scan" ? (
                            <>
                                <p>
                                    Hold QR-koden
                                    foran scanneren.
                                </p>

                                <input
                                    ref={
                                        scanInputRef
                                    }
                                    value={
                                        cardCode
                                    }
                                    onChange={e =>
                                        setCardCode(
                                            e.target.value
                                        )
                                    }
                                    onKeyDown={e => {
                                        if (
                                            e.key ===
                                            "Enter"
                                        ) {
                                            e.preventDefault();

                                            submitScannedOrder();
                                        }
                                    }}
                                    placeholder="Scan eller indtast ID..."
                                    autoComplete="off"
                                    disabled={
                                        busy
                                    }
                                />

                                <button
                                    type="button"
                                    className="pl-primary"
                                    disabled={
                                        !cardCode.trim() ||
                                        busy
                                    }
                                    onClick={() =>
                                        submitScannedOrder()
                                    }
                                >
                                    {busy
                                        ? "Opretter..."
                                        : "Opret ordre"}
                                </button>

                                <small>
                                    QR-format:
                                    /epos/personId/eventId
                                </small>
                            </>
                        ) : !personSearchUnlocked ? (
                            <>
                                <p>
                                    Indtast kode for at
                                    finde en person.
                                </p>

                                <input
                                    type="password"
                                    inputMode="numeric"
                                    value={
                                        personSearchPin
                                    }
                                    onChange={e =>
                                        setPersonSearchPin(
                                            e.target.value
                                        )
                                    }
                                    onKeyDown={e => {
                                        if (
                                            e.key ===
                                            "Enter"
                                        ) {
                                            e.preventDefault();
                                            unlockPersonSearch();
                                        }
                                    }}
                                    placeholder="Kode"
                                    autoFocus
                                />

                                <button
                                    type="button"
                                    className="pl-primary"
                                    disabled={
                                        !personSearchPin ||
                                        busy
                                    }
                                    onClick={
                                        unlockPersonSearch
                                    }
                                >
                                    Fortsæt
                                </button>
                            </>
                        ) : (
                            <>
                                <p>
                                    Søg efter navn.
                                </p>

                                <input
                                    type="text"
                                    value={
                                        personSearch
                                    }
                                    onChange={e => {
                                        setPersonSearch(
                                            e.target.value
                                        );

                                        setSelectedPerson(
                                            null
                                        );
                                    }}
                                    placeholder="Skriv navn..."
                                    autoFocus
                                    disabled={
                                        busy
                                    }
                                />

                                <div
                                    style={{
                                        width: "100%",
                                        maxHeight: "280px",
                                        overflowY: "auto",
                                        display: "grid",
                                        gap: "6px",
                                        marginTop: "10px",
                                    }}
                                >
                                    {personSearching && (
                                        <div className="pl-empty">
                                            Søger...
                                        </div>
                                    )}

                                    {!personSearching &&
                                        personSearch
                                            .trim()
                                            .length >= 2 &&
                                        personResults
                                            .length === 0 && (
                                            <div className="pl-empty">
                                                Ingen personer fundet.
                                            </div>
                                        )}

                                    {personResults.map(
                                        person => {
                                            const selected =
                                                selectedPerson
                                                    ?.personId ===
                                                person.personId;

                                            return (
                                                <button
                                                    key={
                                                        person.personId
                                                    }
                                                    type="button"
                                                    onClick={() =>
                                                        setSelectedPerson(
                                                            person
                                                        )
                                                    }
                                                    style={{
                                                        width: "100%",
                                                        textAlign: "left",
                                                        padding: "10px 12px",
                                                        borderRadius: "8px",
                                                        cursor: "pointer",
                                                        border:
                                                            selected
                                                                ? "2px solid currentColor"
                                                                : "1px solid rgba(0,0,0,0.15)",
                                                        fontWeight:
                                                            selected
                                                                ? 800
                                                                : 600,
                                                    }}
                                                >
                                                    <span
                                                        style={{
                                                            display: "block",
                                                        }}
                                                    >
                                                        {
                                                            person.displayName
                                                        }
                                                    </span>

                                                    <small>
                                                        Person ID{" "}
                                                        {
                                                            person.personId
                                                        }
                                                    </small>
                                                </button>
                                            );
                                        }
                                    )}
                                </div>

                                {selectedPerson && (
                                    <div
                                        style={{
                                            width: "100%",
                                            marginTop: "12px",
                                            padding: "10px 12px",
                                            borderRadius: "8px",
                                            background:
                                                "rgba(65,105,225,0.10)",
                                        }}
                                    >
                                        <strong>
                                            Valgt:{" "}
                                            {
                                                selectedPerson.displayName
                                            }
                                        </strong>
                                    </div>
                                )}

                                <button
                                    type="button"
                                    className="pl-primary"
                                    disabled={
                                        !selectedPerson ||
                                        busy
                                    }
                                    onClick={
                                        submitSelectedPersonOrder
                                    }
                                >
                                    {busy
                                        ? "Opretter..."
                                        : "Opret ordre"}
                                </button>
                            </>
                        )}

                        {message && (
                            <div className="pl-message">
                                {message}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}